import React, { useMemo } from 'react';
import { Calendar, Clock, CalendarDays, Globe, CheckCircle, XCircle } from 'lucide-react';
import { SectionCard, MetricCard, TabLoading, TabError, TabEmpty } from './shared';
import { useUserAvailability } from '../../hooks/useUserTabs';
import { formatNumber } from '../../utils/helpers';
import type { AvailabilityItem } from '../../types/userTabs';

// ============================================================================
// Helpers
// ============================================================================

const DAY_NAMES: Record<number, string> = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

const DAY_NAMES_STR: Record<string, { label: string; order: number }> = {
  SUNDAY: { label: 'Sunday', order: 0 },
  MONDAY: { label: 'Monday', order: 1 },
  TUESDAY: { label: 'Tuesday', order: 2 },
  WEDNESDAY: { label: 'Wednesday', order: 3 },
  THURSDAY: { label: 'Thursday', order: 4 },
  FRIDAY: { label: 'Friday', order: 5 },
  SATURDAY: { label: 'Saturday', order: 6 },
};

function getDayLabel(dayOfWeek: number | string | null): string {
  if (dayOfWeek === null || dayOfWeek === undefined) return 'Unspecified';
  if (typeof dayOfWeek === 'number') return DAY_NAMES[dayOfWeek] ?? `Day ${dayOfWeek}`;
  return DAY_NAMES_STR[dayOfWeek.toUpperCase()]?.label ?? dayOfWeek.charAt(0) + dayOfWeek.slice(1).toLowerCase();
}

function getDayOrder(dayOfWeek: number | string | null): number {
  if (dayOfWeek === null || dayOfWeek === undefined) return 99;
  if (typeof dayOfWeek === 'number') return dayOfWeek;
  return DAY_NAMES_STR[dayOfWeek.toUpperCase()]?.order ?? 99;
}

function formatTimeInTz(isoString: string, timezone: string): string {
  try {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: timezone,
    });
  } catch {
    return isoString;
  }
}

function formatTimeUTC(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'UTC',
  });
}

function getShortTzName(timezone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    }).formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? timezone;
  } catch {
    return timezone;
  }
}

// ============================================================================
// Component
// ============================================================================

interface AvailabilityTabProps {
  userId: number;
}

const AvailabilityTab: React.FC<AvailabilityTabProps> = ({ userId }) => {
  const { data, loading, error, refetch } = useUserAvailability(userId, true);

  const timezone = data?.timezone ?? null;
  const tzAbbr = timezone ? getShortTzName(timezone) : null;

  const groupedByDay = useMemo(() => {
    if (!data?.items?.length) return new Map<string, AvailabilityItem[]>();

    const groups = new Map<string, AvailabilityItem[]>();
    const sorted = [...data.items].sort((a, b) => {
      const dayDiff = getDayOrder(a.dayOfWeek) - getDayOrder(b.dayOfWeek);
      if (dayDiff !== 0) return dayDiff;
      return a.startTime.localeCompare(b.startTime);
    });

    for (const item of sorted) {
      const key = getDayLabel(item.dayOfWeek);
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(item);
    }

    return groups;
  }, [data]);

  const activeCount = useMemo(() => {
    if (!data?.items) return 0;
    return data.items.filter((item: AvailabilityItem) => item.isActive !== false).length;
  }, [data]);

  if (loading) return <TabLoading />;
  if (error) return <TabError message={error} onRetry={refetch} />;
  if (!data) return <TabEmpty message="No availability data available" />;

  const { items, total } = data;

  if (!items || items.length === 0) {
    return <TabEmpty message="No availability slots configured" icon={Calendar} />;
  }

  return (
    <div className="space-y-5">
      {/* Timezone banner */}
      {timezone && (
        <div className="flex items-center gap-2.5 px-4 py-3 rounded-lg border border-border/50 bg-muted/10">
          <Globe className="h-4 w-4 text-blue-400 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-foreground">{timezone}</p>
            <p className="text-[11px] text-muted-foreground">User's timezone ({tzAbbr})</p>
          </div>
        </div>
      )}

      {/* Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <MetricCard
          label="Total Slots"
          value={formatNumber(total)}
          icon={CalendarDays}
          color="text-blue-400"
          bgColor="bg-blue-500/10"
        />
        <MetricCard
          label="Active Slots"
          value={formatNumber(activeCount)}
          icon={CheckCircle}
          color="text-emerald-400"
          bgColor="bg-emerald-500/10"
        />
        <MetricCard
          label="Days Active"
          value={groupedByDay.size}
          icon={Calendar}
          color="text-violet-400"
          bgColor="bg-violet-500/10"
          subtitle="of 7 days"
        />
      </div>

      {/* Availability by Day */}
      <SectionCard
        title="Availability Schedule"
        icon={Clock}
        subtitle={`${items.length} slot${items.length !== 1 ? 's' : ''} across ${groupedByDay.size} day${groupedByDay.size !== 1 ? 's' : ''}`}
      >
        <div className="space-y-4">
          {Array.from(groupedByDay.entries()).map(([day, slots]) => (
            <div key={day} className="rounded-lg border border-border/50 bg-muted/5 overflow-hidden">
              {/* Day Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-border/30 bg-muted/10">
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 rounded-lg bg-blue-500/10">
                    <CalendarDays className="h-3.5 w-3.5 text-blue-400" />
                  </div>
                  <p className="text-sm font-medium text-foreground">{day}</p>
                </div>
                <span className="text-[11px] text-muted-foreground">
                  {slots.length} slot{slots.length !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Time Slots */}
              <div className="divide-y divide-border/30">
                {slots.map((slot) => {
                  const isActive = slot.isActive !== false;

                  return (
                    <div key={slot.id} className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Clock className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                        <div>
                          {/* Primary: user's timezone */}
                          <p className="text-sm font-medium text-foreground">
                            {timezone
                              ? `${formatTimeInTz(slot.startTime, timezone)} – ${formatTimeInTz(slot.endTime, timezone)}`
                              : `${formatTimeUTC(slot.startTime)} – ${formatTimeUTC(slot.endTime)}`}
                            {timezone && (
                              <span className="ml-1.5 text-[11px] text-muted-foreground font-normal">
                                {tzAbbr}
                              </span>
                            )}
                          </p>
                          {/* Secondary: UTC */}
                          <p className="text-[11px] text-muted-foreground mt-0.5">
                            {formatTimeUTC(slot.startTime)} – {formatTimeUTC(slot.endTime)} UTC
                          </p>
                          {/* Date if specific */}
                          {slot.date && (
                            <p className="text-[11px] text-muted-foreground mt-0.5">
                              {new Date(slot.date).toLocaleDateString('en-US', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isActive ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                            <CheckCircle className="h-3 w-3" />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border bg-red-500/10 text-red-400 border-red-500/20">
                            <XCircle className="h-3 w-3" />
                            Inactive
                          </span>
                        )}
                        <span className="text-[10px] text-muted-foreground font-mono">#{slot.id}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
};

export default AvailabilityTab;
